import React from "react";
import rcIcon from "../../assets/images/vital-impact-sub-logo.svg";
import "../../assets/styles/tenantReportPrint.scss";

const TenantReportPrint = ({ tenantReportData }) => {
  const visit = tenantReportData?.finalData?.[0];

  if (!visit) return null;

  const getVisitData = (visitName) => {
    const visits = tenantReportData?.finalData || [];
    return visits.find((visit) => visit.name === visitName) || {};
  };

  const clinical = visit?.patientvisitclinical?.[0];
  const surgery = visit?.patientvisitpostsurgery?.[0];
  const surgeryRecommended = visit?.surgeryrecommended?.[0];
  const seventhDayVisit = getVisitData("7th (Return)");
  const fourteenthDayVisit = getVisitData("14th (Return)");
  const fortiethDayVisit = getVisitData("40th (Return)");

  const getHistoryValue = (label) => {
    const item = clinical?.pastHistories?.find((h) => h.label === label);
    return item?.historylookupvalues?.name || "-";
  };
  const formatHistoryData = (historyArray, visitName) => {
    const filtered = (historyArray || []).filter(
      (item) => item.visit_name === visitName,
    );
    return filtered.map((item) => item.history_name).join(", ") || "-";
  };

  const getClinicalData = (visitData) => {
    const clinical = visitData?.patientvisitclinical?.[0] || {};
    return {
      procedures: clinical.procedures || "-",
      immunizations: clinical.immunizations || "-",
      medications: clinical.medications || "-",
      additional_notes: clinical.additional_notes || "-",
      ophthalmic_history: formatHistoryData(
        tenantReportData?.ophthalmic_history,
        visitData?.name,
      ),
      allergies: formatHistoryData(
        tenantReportData?.allergies,
        visitData?.name,
      ),
      medical_history: formatHistoryData(
        tenantReportData?.medical_history,
        visitData?.name,
      ),
      family_history: formatHistoryData(
        tenantReportData?.family_history,
        visitData?.name,
      ),
    };
  };
  const getExaminationDetailsForVisit = (visitData) => {
    const [leftEye] =
      visitData?.patientvisiteyeexam?.filter(
        (item) => item?.eye_type === "Left Eye",
      ) ?? [];

    const [rightEye] =
      visitData?.patientvisiteyeexam?.filter(
        (item) => item?.eye_type === "Right Eye",
      ) ?? [];

    return {
      leftEye,
      rightEye,
    };
  };

  const leftEye = visit?.patientvisiteyeexam?.find(
    (e) => e.eye_type === "Left Eye",
  );

  const rightEye = visit?.patientvisiteyeexam?.find(
    (e) => e.eye_type === "Right Eye",
  );

  return (
    <div className="tenant-print">
      {/* ================= Header ================= */}
      <div className="header">
        <div className="flex gap-2">
          <img src={rcIcon} className="tenant-header-logo-sec" />
        </div>
      </div>

      {/* ================= Title ================= */}
      <div className="tenant-center">
        <div className="tenant-badge">
          <div>Patient Visit Report</div>
        </div>
      </div>

      {/* ================= Patient Details ================= */}
      <div className="tenant-details-container">
        <div className="left-details">
          <div className="patient-label">
            Camp Name:
            <span className="patient-detail-value">{visit.camp_name}</span>
          </div>

          <div className="patient-label">
            Patient Name:
            <span className="patient-detail-value">{visit.patient_name}</span>
          </div>
        </div>

        <div className="divider"></div>

        <div className="right-details">
          <div className="patient-label">
            Patient ID:
            <span className="patient-detail-value">{visit.patient_code}</span>
          </div>

          <div className="patient-label">
            Patient Aadhaar Number:
            <span className="patient-detail-value">{visit.aadhar_no}</span>
          </div>
        </div>
      </div>

      {/* ================= Initial Visit ================= */}
      <div className="horizontal-dotted-line">
        <div className="vitals-section">
          <div className="vitals-title">Initial Visit</div>

          <div className="vitals-row">
            <div className="vital-item">
              <div className="vital-label">Age</div>
              <div className="vital-value">{visit?.age ?? "-"}</div>
            </div>

            <div className="vital-item">
              <div className="vital-label">Height</div>
              <div className="vital-value">
                {visit?.height ?? clinical?.height ?? "-"}
              </div>
            </div>

            <div className="vital-item">
              <div className="vital-label">Weight</div>
              <div className="vital-value">
                {visit?.weight ?? clinical?.weight ?? "-"}
              </div>
            </div>

            <div className="vital-item">
              <div className="vital-label">Pulse</div>
              <div className="vital-value">
                {visit?.pulse ?? clinical?.pulse ?? "-"} bpm
              </div>
            </div>

            <div className="vital-item">
              <div className="vital-label">Blood Pressure</div>
              <div className="vital-value">
                {visit?.blood_pressure ?? clinical?.blood_pressure ?? "-"} mm Hg
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= Clinical History ================= */}
      <div className="horizontal-dotted-line">
        <div className="history-section">
          <div className="history-row">
            <div className="history-col">
              <div className="history-label">Ophthalmic History</div>
              <div className="history-value">
                {getHistoryValue("ophthalmic_history")}
              </div>
            </div>

            <div className="history-col">
              <div className="history-label">Allergies</div>
              <div className="history-value">
                {getHistoryValue("allergies")}
              </div>
            </div>
          </div>

          <div className="history-row">
            <div className="history-col">
              <div className="history-label">Medical History</div>
              <div className="history-value">
                {getHistoryValue("medical_history")}
              </div>
            </div>

            <div className="history-col">
              <div className="history-label">Family History</div>
              <div className="history-value">
                {getHistoryValue("family_history")}
              </div>
            </div>
          </div>

          <div className="history-row">
            <div className="history-col">
              <div className="history-label">Procedures</div>
              <div className="history-value">{clinical?.procedures || "-"}</div>
            </div>

            <div className="history-col">
              <div className="history-label">Immunizations</div>
              <div className="history-value">
                {clinical?.immunizations || "-"}
              </div>
            </div>
          </div>

          <div className="history-row">
            <div className="history-col">
              <div className="history-label">Medications</div>
              <div className="history-value">
                {clinical?.medications || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="horizontal-dotted-line" />

      <div className="dry-refraction-section">
        <div className="dry-title">Dry Refraction</div>

        <div className="dry-refraction-container">
          {/* LEFT EYE */}
          <div className="eye-box">
            <div className="eye-title">L/DO</div>

            <table className="eye-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Sph</th>
                  <th>Ne.</th>
                  <th>Axis</th>
                  <th>Vis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Distance</td>
                  <td>{leftEye?.distance_sph || "-"}</td>
                  <td>{leftEye?.distance_necylar || "-"}</td>
                  <td>{leftEye?.distance_axis || "-"}</td>
                  <td>{leftEye?.distance_vision || "-"}</td>
                </tr>
                <tr>
                  <td>Near</td>
                  <td>{leftEye?.near_sph || "-"}</td>
                  <td>{leftEye?.near_necylar || "-"}</td>
                  <td>{leftEye?.near_axis || "-"}</td>
                  <td>{leftEye?.near_vision || "-"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* RIGHT EYE */}
          <div className="eye-box">
            <div className="eye-title">R/DO</div>

            <table className="eye-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Sph</th>
                  <th>Ne.</th>
                  <th>Axis</th>
                  <th>Vis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Distance</td>
                  <td>{rightEye?.distance_sph || "-"}</td>
                  <td>{rightEye?.distance_necylar || "-"}</td>
                  <td>{rightEye?.distance_axis || "-"}</td>
                  <td>{rightEye?.distance_vision || "-"}</td>
                </tr>
                <tr>
                  <td>Near</td>
                  <td>{rightEye?.near_sph || "-"}</td>
                  <td>{rightEye?.near_necylar || "-"}</td>
                  <td>{rightEye?.near_axis || "-"}</td>
                  <td>{rightEye?.near_vision || "-"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="horizontal-dotted-line" />

      <div className="eye-exam-section">
        <div className="eye-exam-title">Eye Examination Details</div>

        <div className="eye-exam-container">
          {/* LEFT EYE */}
          <div className="eye-exam-box">
            <div className="eye-exam-subtitle">L/OS</div>

            <div className="eye-exam-grid">
              {[
                ["Visual Acuity", leftEye?.va],
                ["Intraocular Pressure", leftEye?.iop],
                ["Auto Refraction", leftEye?.auto_refraction],
                ["Dilated Refraction", leftEye?.refraction_dilated],
                ["PGP", leftEye?.pgp],
                ["Glasses Prescription", leftEye?.glasses_prescription ? "Yes" : "No",],
                ["Intermediate Glasses Prescription",leftEye?.intermediate_glasses_prescription,],
                ["PMT", leftEye?.pmt],
                ["Retinoscopy", leftEye?.retinoscopy],
                ["Keratometry", leftEye?.keratometry],
                ["Amsler", leftEye?.amsler],
                ["Contact Lens Prescription",leftEye?.contact_lens_prescription,],
                ["Color Vision", leftEye?.color_vision],
                ["Contrast Sensitivity", leftEye?.contrast_sensitivity],
                ["Orthoptics", leftEye?.orthoptics],
                ["Other", leftEye?.others],
              ].map(([label, value], i) => (
                <div className="exam-item" key={i}>
                  <span>{label}</span>
                  <span>{value || "-"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT EYE */}
          <div className="eye-exam-box">
            <div className="eye-exam-subtitle">R/DO</div>

            <div className="eye-exam-grid">
              {[
                ["Visual Acuity", rightEye?.va],
                ["Intraocular Pressure", rightEye?.iop],
                ["Auto Refraction", rightEye?.auto_refraction],
                ["Dilated Refraction", rightEye?.refraction_dilated],
                ["PGP", rightEye?.pgp],
                ["Glasses Prescription", rightEye?.glasses_prescription ? "Yes" : "No",],
                ["Intermediate Glasses Prescription",rightEye?.intermediate_glasses_prescription,],
                ["PMT", rightEye?.pmt],
                ["Retinoscopy", rightEye?.retinoscopy],
                ["Keratometry", rightEye?.keratometry],
                ["Amsler", rightEye?.amsler],
                ["Contact Lens Prescription",rightEye?.contact_lens_prescription,],
                ["Color Vision", rightEye?.color_vision],
                ["Contrast Sensitivity", rightEye?.contrast_sensitivity],
                ["Orthoptics", rightEye?.orthoptics],
                ["Other", rightEye?.others],
              ].map(([label, value], i) => (
                <div className="exam-item" key={i}>
                  <span>{label}</span>
                  <span>{value || "-"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="treatment-section">
        {/* TOP DETAILS */}
        <div className="treatment-row">
          <div className="treatment-col">
            <div className="treatment-label">Patient Condition</div>
            <div className="treatment-value">
              {surgeryRecommended?.lookup?.name || "-"}
            </div>
          </div>

          <div className="treatment-col">
            <div className="treatment-label">Treatment Recommended</div>
            <div className="treatment-value">
              {surgeryRecommended?.lookup?.name || "-"}
            </div>
          </div>

          <div className="treatment-col">
            <div className="treatment-label">Date of Transportation</div>
            <div className="treatment-value">
              {surgery?.date_of_surgery
                ? new Date(surgery.date_of_surgery).toLocaleDateString()
                : "-"}
            </div>
          </div>
        </div>

        {/* DOTTED LINE */}
        <div className="horizontal-dotted-line"></div>

        {/* SURGERY SECTION */}
        <div className="section-title">Surgery</div>

        <div className="treatment-row">
          <div className="treatment-col">
            <div className="treatment-label">Date</div>
            <div className="treatment-value">
              {surgery?.date_of_surgery
                ? new Date(surgery.date_of_surgery).toLocaleDateString()
                : "-"}
            </div>
          </div>

          <div className="vertical-dotted-line" />

          <div className="treatment-col">
            <div className="treatment-label">Treated by</div>
            <div className="treatment-value">{surgery?.treated_by || "-"}</div>
          </div>

          <div className="vertical-dotted-line" />

          <div className="treatment-col">
            <div className="treatment-label">Surgery Type</div>
            <div className="treatment-value">
              {surgery?.surgery_type || "-"}
            </div>
          </div>

          <div className="vertical-dotted-line" />

          <div className="treatment-col">
            <div className="treatment-label">Lens Type</div>
            <div className="treatment-value">{surgery?.lens_type || "-"}</div>
          </div>
        </div>

        {/* SUMMARY */}
        <div className="summary-section">
          <div className="treatment-label">Summary</div>
          <div className="treatment-value">
            {surgery?.treatment_summary || "-"}
          </div>
        </div>
      </div>

      {(() => {
        const clinical = getClinicalData(seventhDayVisit);

        return (
          <>
            {/* ================= 7th Day Header ================= */}
            <div className="horizontal-dotted-line">
              <div className="heading mt-3 md:mt-0">7 th Day</div>

              {/* ================= Patient Vitals ================= */}
              <div className="md:pt-4 pt-2">
                <div className="parameter">Patient Vitals</div>

                <div className="vitals-row">
                  <div className="vital-item">
                    <div className="vital-label">Age</div>
                    <div className="vital-value">
                      {seventhDayVisit?.age ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Height</div>
                    <div className="vital-value">
                      {seventhDayVisit?.height ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Weight</div>
                    <div className="vital-value">
                      {seventhDayVisit?.weight ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Pulse</div>
                    <div className="vital-value">
                      {seventhDayVisit?.pulse
                        ? `${seventhDayVisit.pulse} bpm`
                        : "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Blood Pressure</div>
                    <div className="vital-value">
                      {seventhDayVisit?.blood_pressure
                        ? `${seventhDayVisit.blood_pressure} mm Hg`
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= Clinical History ================= */}
            <div className="horizontal-dotted-line">
              <div className="history-section">
                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Ophthalmic History</div>
                    <div className="history-value">
                      {clinical.ophthalmic_history || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Allergies</div>
                    <div className="history-value">
                      {clinical.allergies || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Medical History</div>
                    <div className="history-value">
                      {clinical.medical_history || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Family History</div>
                    <div className="history-value">
                      {clinical.family_history || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Procedures</div>
                    <div className="history-value">
                      {clinical.procedures || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Immunizations</div>
                    <div className="history-value">
                      {clinical.immunizations || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Medications</div>
                    <div className="history-value">
                      {clinical.medications || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* <div className="horizontal-dotted-line" /> */}
      {(() => {
        const [leftEye7th] =
          seventhDayVisit?.patientvisiteyeexam?.filter(
            (item) => item?.eye_type === "Left Eye",
          ) ?? [];

        const [rightEye7th] =
          seventhDayVisit?.patientvisiteyeexam?.filter(
            (item) => item?.eye_type === "Right Eye",
          ) ?? [];

        return (
          <>
            <div className="horizontal-dotted-line" />

            <div className="dry-refraction-section">
              <div className="dry-title">Dry Refraction</div>

              <div className="dry-refraction-container">
                {/* LEFT EYE */}
                <div className="eye-box">
                  <div className="eye-title">L/DO</div>

                  <table className="eye-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sph</th>
                        <th>Ne.</th>
                        <th>Axis</th>
                        <th>Vis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Distance</td>
                        <td>{leftEye7th?.distance_sph || "-"}</td>
                        <td>{leftEye7th?.distance_necylar || "-"}</td>
                        <td>{leftEye7th?.distance_axis || "-"}</td>
                        <td>{leftEye7th?.distance_vision || "-"}</td>
                      </tr>
                      <tr>
                        <td>Near</td>
                        <td>{leftEye7th?.near_sph || "-"}</td>
                        <td>{leftEye7th?.near_necylar || "-"}</td>
                        <td>{leftEye7th?.near_axis || "-"}</td>
                        <td>{leftEye7th?.near_vision || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* RIGHT EYE */}
                <div className="eye-box">
                  <div className="eye-title">R/DO</div>

                  <table className="eye-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sph</th>
                        <th>Ne.</th>
                        <th>Axis</th>
                        <th>Vis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Distance</td>
                        <td>{rightEye7th?.distance_sph || "-"}</td>
                        <td>{rightEye7th?.distance_necylar || "-"}</td>
                        <td>{rightEye7th?.distance_axis || "-"}</td>
                        <td>{rightEye7th?.distance_vision || "-"}</td>
                      </tr>
                      <tr>
                        <td>Near</td>
                        <td>{rightEye7th?.near_sph || "-"}</td>
                        <td>{rightEye7th?.near_necylar || "-"}</td>
                        <td>{rightEye7th?.near_axis || "-"}</td>
                        <td>{rightEye7th?.near_vision || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const { leftEye, rightEye } =
          getExaminationDetailsForVisit(seventhDayVisit);
        return (
          <>
            <div className="eye-exam-section">
              <div className="eye-exam-title">Eye Examination Details</div>

              <div className="eye-exam-container">
                {/* LEFT EYE */}
                <div className="eye-exam-box">
                  <div className="eye-exam-subtitle">L/OS</div>

                  <div className="eye-exam-grid">
                    {[
                      ["Visual Acuity", leftEye?.va],
                      ["Intraocular Pressure", leftEye?.iop],
                      ["Auto Refraction", leftEye?.auto_refraction],
                      ["Dilated Refraction", leftEye?.refraction_dilated],
                      ["PGP", leftEye?.pgp],
                      ["Glasses Prescription",leftEye?.glasses_prescription ? "Yes" : "No",],
                      ["Intermediate Glasses Prescription",leftEye?.intermediate_glasses_prescription,],
                      ["PMT", leftEye?.pmt],
                      ["Retinoscopy", leftEye?.retinoscopy],
                      ["Keratometry", leftEye?.keratometry],
                      ["Amsler", leftEye?.amsler],
                      ["Contact Lens Prescription",leftEye?.contact_lens_prescription,],
                      ["Color Vision", leftEye?.color_vision],
                      ["Contrast Sensitivity", leftEye?.contrast_sensitivity],
                      ["Orthoptics", leftEye?.orthoptics],
                      ["Other", leftEye?.others],
                    ].map(([label, value], i) => (
                      <div className="exam-item" key={i}>
                        <span>{label}</span>
                        <span>{value || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT EYE */}
                <div className="eye-exam-box">
                  <div className="eye-exam-subtitle">R/DO</div>

                  <div className="eye-exam-grid">
                    {[
                      ["Visual Acuity", rightEye?.va],
                      ["Intraocular Pressure", rightEye?.iop],
                      ["Auto Refraction", rightEye?.auto_refraction],
                      ["Dilated Refraction", rightEye?.refraction_dilated],
                      ["PGP", rightEye?.pgp],
                      ["Glasses Prescription",rightEye?.glasses_prescription ? "Yes" : "No",],
                      ["Intermediate Glasses Prescription", rightEye?.intermediate_glasses_prescription,],
                      ["PMT", rightEye?.pmt],
                      ["Retinoscopy", rightEye?.retinoscopy],
                      ["Keratometry", rightEye?.keratometry],
                      ["Amsler", rightEye?.amsler],
                      ["Contact Lens Prescription",rightEye?.contact_lens_prescription,],
                      ["Color Vision", rightEye?.color_vision],
                      ["Contrast Sensitivity", rightEye?.contrast_sensitivity],
                      ["Orthoptics", rightEye?.orthoptics],
                      ["Other", rightEye?.others],
                    ].map(([label, value], i) => (
                      <div className="exam-item" key={i}>
                        <span>{label}</span>
                        <span>{value || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      <div className="horizontal-dotted-line" />

      {(() => {
        const prescriptions = seventhDayVisit?.medicalprescription ?? [];

        return (
          <div className="pt-4">
            <div className="medical-priscription-title">
              Medical Prescription
            </div>

            {prescriptions.length === 0 ? (
              <div className="medical-row pt-2 pb-4">
                {["Name", "Dosage", "Type", "Frequency", "Route", "Period"].map(
                  (label, i) => (
                    <div key={i} className="vital-item">
                      <div className="mark">{label}</div>
                      <div className="brief">-</div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              prescriptions.map((item, index) => (
                <div
                  key={item?.medical_prescription_id ?? index}
                  className="medical-row pt-2 pb-4"
                >
                  <div className="vital-item">
                    <div className="mark">Name</div>
                    <div className="brief">
                      {item?.medicineLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Dosage</div>
                    <div className="brief">
                      {item?.dosageLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Type</div>
                    <div className="brief">{item?.typeLookup?.name || "-"}</div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Frequency</div>
                    <div className="brief">
                      {item?.frequencyLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Route</div>
                    <div className="brief">
                      {item?.routeLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center md:text-right">
                    <div className="mark">Period</div>
                    <div className="brief">
                      {item?.periodLookup?.name || "-"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })()}

      {(() => {
        const clinical = getClinicalData(fourteenthDayVisit);

        return (
          <>
            <div className="horizontal-dotted-line">
              <div className="heading mt-3 md:mt-0">14 th Day</div>

              {/* ================= Patient Vitals ================= */}
              <div className="md:pt-4 pt-2">
                <div className="parameter">Patient Vitals</div>

                <div className="vitals-row">
                  <div className="vital-item">
                    <div className="vital-label">Age</div>
                    <div className="vital-value">
                      {fourteenthDayVisit?.age ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Height</div>
                    <div className="vital-value">
                      {fourteenthDayVisit?.height ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Weight</div>
                    <div className="vital-value">
                      {fourteenthDayVisit?.weight ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Pulse</div>
                    <div className="vital-value">
                      {fourteenthDayVisit?.pulse
                        ? `${fourteenthDayVisit.pulse} bpm`
                        : "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Blood Pressure</div>
                    <div className="vital-value">
                      {fourteenthDayVisit?.blood_pressure
                        ? `${fourteenthDayVisit.blood_pressure} mm Hg`
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= Clinical History ================= */}
            <div className="horizontal-dotted-line">
              <div className="history-section">
                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Ophthalmic History</div>
                    <div className="history-value">
                      {clinical.ophthalmic_history || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Allergies</div>
                    <div className="history-value">
                      {clinical.allergies || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Medical History</div>
                    <div className="history-value">
                      {clinical.medical_history || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Family History</div>
                    <div className="history-value">
                      {clinical.family_history || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Procedures</div>
                    <div className="history-value">
                      {clinical.procedures || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Immunizations</div>
                    <div className="history-value">
                      {clinical.immunizations || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Medications</div>
                    <div className="history-value">
                      {clinical.medications || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const [leftEye14th] =
          fourteenthDayVisit?.patientvisiteyeexam?.filter(
            (item) => item?.eye_type === "Left Eye",
          ) ?? [];

        const [rightEye14th] =
          fourteenthDayVisit?.patientvisiteyeexam?.filter(
            (item) => item?.eye_type === "Right Eye",
          ) ?? [];

        return (
          <>
            <div className="horizontal-dotted-line" />

            <div className="dry-refraction-section">
              <div className="dry-title">Dry Refraction</div>

              <div className="dry-refraction-container">
                {/* LEFT EYE */}
                <div className="eye-box">
                  <div className="eye-title">L/DO</div>

                  <table className="eye-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sph</th>
                        <th>Ne.</th>
                        <th>Axis</th>
                        <th>Vis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Distance</td>
                        <td>{leftEye14th?.distance_sph || "-"}</td>
                        <td>{leftEye14th?.distance_necylar || "-"}</td>
                        <td>{leftEye14th?.distance_axis || "-"}</td>
                        <td>{leftEye14th?.distance_vision || "-"}</td>
                      </tr>
                      <tr>
                        <td>Near</td>
                        <td>{leftEye14th?.near_sph || "-"}</td>
                        <td>{leftEye14th?.near_necylar || "-"}</td>
                        <td>{leftEye14th?.near_axis || "-"}</td>
                        <td>{leftEye14th?.near_vision || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* RIGHT EYE */}
                <div className="eye-box">
                  <div className="eye-title">R/DO</div>

                  <table className="eye-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sph</th>
                        <th>Ne.</th>
                        <th>Axis</th>
                        <th>Vis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Distance</td>
                        <td>{rightEye14th?.distance_sph || "-"}</td>
                        <td>{rightEye14th?.distance_necylar || "-"}</td>
                        <td>{rightEye14th?.distance_axis || "-"}</td>
                        <td>{rightEye14th?.distance_vision || "-"}</td>
                      </tr>
                      <tr>
                        <td>Near</td>
                        <td>{rightEye14th?.near_sph || "-"}</td>
                        <td>{rightEye14th?.near_necylar || "-"}</td>
                        <td>{rightEye14th?.near_axis || "-"}</td>
                        <td>{rightEye14th?.near_vision || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const { leftEye, rightEye } =
          getExaminationDetailsForVisit(fourteenthDayVisit);
        return (
          <>
            <div className="eye-exam-section">
              <div className="eye-exam-title">Eye Examination Details</div>

              <div className="eye-exam-container">
                {/* LEFT EYE */}
                <div className="eye-exam-box">
                  <div className="eye-exam-subtitle">L/OS</div>

                  <div className="eye-exam-grid">
                    {[
                      ["Visual Acuity", leftEye?.va],
                      ["Intraocular Pressure", leftEye?.iop],
                      ["Auto Refraction", leftEye?.auto_refraction],
                      ["Dilated Refraction", leftEye?.refraction_dilated],
                      ["PGP", leftEye?.pgp],
                      ["Glasses Prescription",leftEye?.glasses_prescription ? "Yes" : "No",],
                      ["Intermediate Glasses Prescription",leftEye?.intermediate_glasses_prescription,],
                      ["PMT", leftEye?.pmt],
                      ["Retinoscopy", leftEye?.retinoscopy],
                      ["Keratometry", leftEye?.keratometry],
                      ["Amsler", leftEye?.amsler],
                      ["Contact Lens Prescription",leftEye?.contact_lens_prescription,],
                      ["Color Vision", leftEye?.color_vision],
                      ["Contrast Sensitivity", leftEye?.contrast_sensitivity],
                      ["Orthoptics", leftEye?.orthoptics],
                      ["Other", leftEye?.others],
                    ].map(([label, value], i) => (
                      <div className="exam-item" key={i}>
                        <span>{label}</span>
                        <span>{value || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT EYE */}
                <div className="eye-exam-box">
                  <div className="eye-exam-subtitle">R/DO</div>

                  <div className="eye-exam-grid">
                    {[
                      ["Visual Acuity", rightEye?.va],
                      ["Intraocular Pressure", rightEye?.iop],
                      ["Auto Refraction", rightEye?.auto_refraction],
                      ["Dilated Refraction", rightEye?.refraction_dilated],
                      ["PGP", rightEye?.pgp],
                      [ "Glasses Prescription",rightEye?.glasses_prescription ? "Yes" : "No",],
                      ["Intermediate Glasses Prescription", rightEye?.intermediate_glasses_prescription,],
                      ["PMT", rightEye?.pmt],
                      ["Retinoscopy", rightEye?.retinoscopy],
                      ["Keratometry", rightEye?.keratometry],
                      ["Amsler", rightEye?.amsler],
                      ["Contact Lens Prescription",rightEye?.contact_lens_prescription,],
                      ["Color Vision", rightEye?.color_vision],
                      ["Contrast Sensitivity", rightEye?.contrast_sensitivity],
                      ["Orthoptics", rightEye?.orthoptics],
                      ["Other", rightEye?.others],
                    ].map(([label, value], i) => (
                      <div className="exam-item" key={i}>
                        <span>{label}</span>
                        <span>{value || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const prescriptions = fourteenthDayVisit?.medicalprescription ?? [];

        return (
          <div className="pt-4">
            <div className="medical-priscription-title">
              Medical Prescription
            </div>

            {prescriptions.length === 0 ? (
              <div className="medical-row pt-2 pb-4">
                {["Name", "Dosage", "Type", "Frequency", "Route", "Period"].map(
                  (label, i) => (
                    <div key={i} className="vital-item">
                      <div className="mark">{label}</div>
                      <div className="brief">-</div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              prescriptions.map((item, index) => (
                <div
                  key={item?.medical_prescription_id ?? index}
                  className="medical-row pt-2 pb-4"
                >
                  <div className="vital-item">
                    <div className="mark">Name</div>
                    <div className="brief">
                      {item?.medicineLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Dosage</div>
                    <div className="brief">
                      {item?.dosageLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Type</div>
                    <div className="brief">{item?.typeLookup?.name || "-"}</div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Frequency</div>
                    <div className="brief">
                      {item?.frequencyLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Route</div>
                    <div className="brief">
                      {item?.routeLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center md:text-right">
                    <div className="mark">Period</div>
                    <div className="brief">
                      {item?.periodLookup?.name || "-"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })()}

      {(() => {
        const clinical = getClinicalData(fortiethDayVisit);

        return (
          <>
            <div className="horizontal-dotted-line">
              <div className="heading mt-3 md:mt-0">40 th Day</div>

              {/* ================= Patient Vitals ================= */}
              <div className="md:pt-4 pt-2">
                <div className="parameter">Patient Vitals</div>

                <div className="vitals-row">
                  <div className="vital-item">
                    <div className="vital-label">Age</div>
                    <div className="vital-value">
                      {fortiethDayVisit?.age ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Height</div>
                    <div className="vital-value">
                      {fortiethDayVisit?.height ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Weight</div>
                    <div className="vital-value">
                      {fortiethDayVisit?.weight ?? "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Pulse</div>
                    <div className="vital-value">
                      {fortiethDayVisit?.pulse
                        ? `${fortiethDayVisit.pulse} bpm`
                        : "-"}
                    </div>
                  </div>

                  <div className="vital-item">
                    <div className="vital-label">Blood Pressure</div>
                    <div className="vital-value">
                      {fortiethDayVisit?.blood_pressure
                        ? `${fortiethDayVisit.blood_pressure} mm Hg`
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= Clinical History ================= */}
            <div className="horizontal-dotted-line">
              <div className="history-section">
                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Ophthalmic History</div>
                    <div className="history-value">
                      {clinical.ophthalmic_history || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Allergies</div>
                    <div className="history-value">
                      {clinical.allergies || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Medical History</div>
                    <div className="history-value">
                      {clinical.medical_history || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Family History</div>
                    <div className="history-value">
                      {clinical.family_history || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Procedures</div>
                    <div className="history-value">
                      {clinical.procedures || "-"}
                    </div>
                  </div>

                  <div className="history-col">
                    <div className="history-label">Immunizations</div>
                    <div className="history-value">
                      {clinical.immunizations || "-"}
                    </div>
                  </div>
                </div>

                <div className="history-row">
                  <div className="history-col">
                    <div className="history-label">Medications</div>
                    <div className="history-value">
                      {clinical.medications || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const [leftEye40th] =
          fortiethDayVisit?.patientvisiteyeexam?.filter(
            (item) => item?.eye_type === "Left Eye",
          ) ?? [];

        const [rightEye40th] =
          fortiethDayVisit?.patientvisiteyeexam?.filter(
            (item) => item?.eye_type === "Right Eye",
          ) ?? [];

        return (
          <>
            <div className="horizontal-dotted-line" />

            <div className="dry-refraction-section">
              <div className="dry-title">Dry Refraction</div>

              <div className="dry-refraction-container">
                {/* LEFT EYE */}
                <div className="eye-box">
                  <div className="eye-title">L/DO</div>

                  <table className="eye-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sph</th>
                        <th>Ne.</th>
                        <th>Axis</th>
                        <th>Vis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Distance</td>
                        <td>{leftEye40th?.distance_sph || "-"}</td>
                        <td>{leftEye40th?.distance_necylar || "-"}</td>
                        <td>{leftEye40th?.distance_axis || "-"}</td>
                        <td>{leftEye40th?.distance_vision || "-"}</td>
                      </tr>
                      <tr>
                        <td>Near</td>
                        <td>{leftEye40th?.near_sph || "-"}</td>
                        <td>{leftEye40th?.near_necylar || "-"}</td>
                        <td>{leftEye40th?.near_axis || "-"}</td>
                        <td>{leftEye40th?.near_vision || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* RIGHT EYE */}
                <div className="eye-box">
                  <div className="eye-title">R/DO</div>

                  <table className="eye-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sph</th>
                        <th>Ne.</th>
                        <th>Axis</th>
                        <th>Vis</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Distance</td>
                        <td>{rightEye40th?.distance_sph || "-"}</td>
                        <td>{rightEye40th?.distance_necylar || "-"}</td>
                        <td>{rightEye40th?.distance_axis || "-"}</td>
                        <td>{rightEye40th?.distance_vision || "-"}</td>
                      </tr>
                      <tr>
                        <td>Near</td>
                        <td>{rightEye40th?.near_sph || "-"}</td>
                        <td>{rightEye40th?.near_necylar || "-"}</td>
                        <td>{rightEye40th?.near_axis || "-"}</td>
                        <td>{rightEye40th?.near_vision || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const { leftEye, rightEye } =
          getExaminationDetailsForVisit(fortiethDayVisit);
        return (
          <>
            <div className="eye-exam-section">
              <div className="eye-exam-title">Eye Examination Details</div>

              <div className="eye-exam-container">
                {/* LEFT EYE */}
                <div className="eye-exam-box">
                  <div className="eye-exam-subtitle">L/OS</div>

                  <div className="eye-exam-grid">
                    {[
                      ["Visual Acuity", leftEye?.va],
                      ["Intraocular Pressure", leftEye?.iop],
                      ["Auto Refraction", leftEye?.auto_refraction],
                      ["Dilated Refraction", leftEye?.refraction_dilated],
                      ["PGP", leftEye?.pgp],
                      ["Glasses Prescription",leftEye?.glasses_prescription ? "Yes" : "No",],
                      ["Intermediate Glasses Prescription",leftEye?.intermediate_glasses_prescription,],
                      ["PMT", leftEye?.pmt],
                      ["Retinoscopy", leftEye?.retinoscopy],
                      ["Keratometry", leftEye?.keratometry],
                      ["Amsler", leftEye?.amsler],
                      ["Contact Lens Prescription", leftEye?.contact_lens_prescription,],
                      ["Color Vision", leftEye?.color_vision],
                      ["Contrast Sensitivity", leftEye?.contrast_sensitivity],
                      ["Orthoptics", leftEye?.orthoptics],
                      ["Other", leftEye?.others],
                    ].map(([label, value], i) => (
                      <div className="exam-item" key={i}>
                        <span>{label}</span>
                        <span>{value || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT EYE */}
                <div className="eye-exam-box">
                  <div className="eye-exam-subtitle">R/DO</div>

                  <div className="eye-exam-grid">
                    {[
                      ["Visual Acuity", rightEye?.va],
                      ["Intraocular Pressure", rightEye?.iop],
                      ["Auto Refraction", rightEye?.auto_refraction],
                      ["Dilated Refraction", rightEye?.refraction_dilated],
                      ["PGP", rightEye?.pgp],
                      ["Glasses Prescription",rightEye?.glasses_prescription ? "Yes" : "No", ],
                      ["Intermediate Glasses Prescription",rightEye?.intermediate_glasses_prescription,],
                      ["PMT", rightEye?.pmt],
                      ["Retinoscopy", rightEye?.retinoscopy],
                      ["Keratometry", rightEye?.keratometry],
                      ["Amsler", rightEye?.amsler],
                      ["Contact Lens Prescription", rightEye?.contact_lens_prescription,],
                      ["Color Vision", rightEye?.color_vision],
                      ["Contrast Sensitivity", rightEye?.contrast_sensitivity],
                      ["Orthoptics", rightEye?.orthoptics],
                      ["Other", rightEye?.others],
                    ].map(([label, value], i) => (
                      <div className="exam-item" key={i}>
                        <span>{label}</span>
                        <span>{value || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {(() => {
        const prescriptions = fortiethDayVisit?.medicalprescription ?? [];

        return (
          <div className="pt-4">
            <div className="medical-priscription-title">
              Medical Prescription
            </div>

            {prescriptions.length === 0 ? (
              <div className="medical-row pt-2 pb-4">
                {["Name", "Dosage", "Type", "Frequency", "Route", "Period"].map(
                  (label, i) => (
                    <div key={i} className="vital-item">
                      <div className="mark">{label}</div>
                      <div className="brief">-</div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              prescriptions.map((item, index) => (
                <div
                  key={item?.medical_prescription_id ?? index}
                  className="medical-row pt-2 pb-4"
                >
                  <div className="vital-item">
                    <div className="mark">Name</div>
                    <div className="brief">
                      {item?.medicineLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Dosage</div>
                    <div className="brief">
                      {item?.dosageLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Type</div>
                    <div className="brief">{item?.typeLookup?.name || "-"}</div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Frequency</div>
                    <div className="brief">
                      {item?.frequencyLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center">
                    <div className="mark">Route</div>
                    <div className="brief">
                      {item?.routeLookup?.name || "-"}
                    </div>
                  </div>

                  <div className="vital-item text-center md:text-right">
                    <div className="mark">Period</div>
                    <div className="brief">
                      {item?.periodLookup?.name || "-"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default TenantReportPrint;
